import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import axios from 'axios';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';
import { EyeIcon, EyeOffIcon, LockIcon, CheckIcon } from 'lucide-react';
import Header from './Header';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const token = searchParams.get('token');

  const [formData, setFormData] = useState({
    new_password: '',
    confirm_password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [tokenValid, setTokenValid] = useState(null);

  useEffect(() => {
    if (!token) {
      toast.error('Geçersiz sıfırlama linki');
      navigate('/login');
      return;
    }
    
    // Validate token with backend
    const validateToken = async () => {
      try {
        // Try to verify token by making a GET request
        await axios.get(`${BACKEND_URL}/api/auth/validate-reset-token?token=${token}`);
        setTokenValid(true);
      } catch (error) {
        console.error('Token validation error:', error);
        setTokenValid(false);
        toast.error('Bu sıfırlama linki geçersiz veya süresi dolmuş. Lütfen yeni bir link talep edin.');
        setTimeout(() => {
          navigate('/forgot-password');
        }, 2000);
      }
    };
    
    validateToken();
  }, [token, navigate]);

  const validatePassword = (password) => {
    const minLength = password.length >= 6;
    const hasUpper = /[A-Z]/.test(password);
    const hasLower = /[a-z]/.test(password);
    const hasNumber = /\d/.test(password);
    const hasSpecial = /[!@#$%^&*(),.?":{}|<>]/.test(password);
    
    return {
      minLength,
      hasUpper,
      hasLower,
      hasNumber,
      hasSpecial,
      isValid: minLength && hasUpper && hasLower && hasNumber && hasSpecial
    };
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.new_password !== formData.confirm_password) {
      toast.error('Şifreler eşleşmiyor');
      return;
    }

    const passwordValidation = validatePassword(formData.new_password);
    if (!passwordValidation.isValid) {
      toast.error('Şifre gereksinimlerini karşılamıyor');
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/reset-password`, {
        token,
        new_password: formData.new_password
      });

      // Store the token
      localStorage.setItem('token', response.data.access_token);
      await checkAuth();
      
      toast.success('Şifreniz başarıyla güncellendi!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Şifre sıfırlama başarısız');
    } finally {
      setLoading(false);
    }
  };

  if (tokenValid === null) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-50 via-cyan-50 to-sky-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="text-center">
          <div className="loading-spinner mx-auto mb-4"></div>
          <span>Doğrulanıyor...</span>
        </div>
      </div>
    );
  }

  const passwordValidation = validatePassword(formData.new_password);

  return (
    <>
      <Header />
      <div className="min-h-screen bg-gradient-to-br from-teal-50 via-cyan-50 to-sky-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full">
          {/* Page Title */}
          <div className="text-center mb-8">
          <h2 className="mt-4 text-3xl font-bold text-gray-900">
            Yeni Şifre Oluştur
          </h2>
          <p className="mt-2 text-gray-600">
            Güvenli bir şifre oluşturun
          </p>
        </div>

        {/* Reset Password Form */}
        <Card className="p-8 shadow-xl border-0">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label htmlFor="new_password" className="form-label">
                Yeni Şifre
              </Label>
              <div className="relative">
                <Input
                  id="new_password"
                  name="new_password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.new_password}
                  onChange={handleChange}
                  className="form-input pr-10"
                  placeholder="••••••••"
                  required
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeOffIcon className="h-4 w-4 text-gray-400" />
                  ) : (
                    <EyeIcon className="h-4 w-4 text-gray-400" />
                  )}
                </button>
              </div>
            </div>

            <div>
              <Label htmlFor="confirm_password" className="form-label">
                Şifre Tekrar
              </Label>
              <div className="relative">
                <Input
                  id="confirm_password"
                  name="confirm_password"
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={formData.confirm_password}
                  onChange={handleChange}
                  className="form-input pr-10"
                  placeholder="••••••••"
                  required
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? (
                    <EyeOffIcon className="h-4 w-4 text-gray-400" />
                  ) : (
                    <EyeIcon className="h-4 w-4 text-gray-400" />
                  )}
                </button>
              </div>
            </div>

            {/* Password Requirements */}
            {formData.new_password && (
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="text-sm font-semibold text-gray-700 mb-3">Şifre Gereksinimleri:</h4>
                <div className="space-y-2 text-sm">
                  <div className={`flex items-center ${passwordValidation.minLength ? 'text-green-600' : 'text-gray-500'}`}>
                    <CheckIcon className={`w-4 h-4 mr-2 ${passwordValidation.minLength ? 'text-green-600' : 'text-gray-400'}`} />
                    En az 6 karakter
                  </div>
                  <div className={`flex items-center ${passwordValidation.hasUpper ? 'text-green-600' : 'text-gray-500'}`}>
                    <CheckIcon className={`w-4 h-4 mr-2 ${passwordValidation.hasUpper ? 'text-green-600' : 'text-gray-400'}`} />
                    Büyük harf (A-Z)
                  </div>
                  <div className={`flex items-center ${passwordValidation.hasLower ? 'text-green-600' : 'text-gray-500'}`}>
                    <CheckIcon className={`w-4 h-4 mr-2 ${passwordValidation.hasLower ? 'text-green-600' : 'text-gray-400'}`} />
                    Küçük harf (a-z)
                  </div>
                  <div className={`flex items-center ${passwordValidation.hasNumber ? 'text-green-600' : 'text-gray-500'}`}>
                    <CheckIcon className={`w-4 h-4 mr-2 ${passwordValidation.hasNumber ? 'text-green-600' : 'text-gray-400'}`} />
                    Rakam (0-9)
                  </div>
                  <div className={`flex items-center ${passwordValidation.hasSpecial ? 'text-green-600' : 'text-gray-500'}`}>
                    <CheckIcon className={`w-4 h-4 mr-2 ${passwordValidation.hasSpecial ? 'text-green-600' : 'text-gray-400'}`} />
                    Özel karakter (!@#$%^&*)
                  </div>
                </div>
              </div>
            )}

            {/* Password Match Check */}
            {formData.confirm_password && (
              <div className={`flex items-center text-sm ${
                formData.new_password === formData.confirm_password 
                  ? 'text-green-600' 
                  : 'text-red-600'
              }`}>
                <CheckIcon className={`w-4 h-4 mr-2 ${
                  formData.new_password === formData.confirm_password 
                    ? 'text-green-600' 
                    : 'text-red-400'
                }`} />
                {formData.new_password === formData.confirm_password 
                  ? 'Şifreler eşleşiyor' 
                  : 'Şifreler eşleşmiyor'
                }
              </div>
            )}

            <Button
              type="submit"
              disabled={loading || !passwordValidation.isValid || formData.new_password !== formData.confirm_password}
              className="btn-primary w-full flex justify-center py-3"
            >
              {loading ? (
                <div className="loading-spinner mr-2"></div>
              ) : (
                <LockIcon className="w-4 h-4 mr-2" />
              )}
              <span>{loading ? 'Güncelleniyor...' : 'Şifremi Güncelle'}</span>
            </Button>
          </form>

          <div className="mt-6 text-center">
            <Link to="/login" className="font-medium text-teal-600 hover:text-teal-500">
              Girişe Geri Dön
            </Link>
          </div>
        </Card>

        {/* Security Notice */}
        <Card className="mt-4 p-4 bg-green-50 border border-green-200">
          <h3 className="text-sm font-semibold text-green-800 mb-2">Güvenlik:</h3>
          <div className="text-sm text-green-700 space-y-1">
            <p>• Şifre sıfırlama linki tek kullanımlıktır</p>
            <p>• Yeni şifreniz şifrelenerek saklanır</p>
            <p>• İşlem tamamlandıktan sonra otomatik giriş yapılır</p>
          </div>
        </Card>
      </div>
    </div>
    </>
  );
};

export default ResetPassword;